import { BoxGeometry, CylinderGeometry, DoubleSide, Group, Mesh, MeshStandardMaterial, PlaneGeometry, type Material } from 'three';
import { LAYOUT } from '../road/roadLayout';

const STEEL = new MeshStandardMaterial({ color: '#7a7f86', metalness: 0.6, roughness: 0.45 });
const FRAME = new MeshStandardMaterial({ color: '#2d3035', metalness: 0.4, roughness: 0.6 });

export const BILLBOARD = { width: 12, height: 4, poleHeight: 8.5 } as const;
export const GANTRY = { width: 14, height: 3.4, clearance: 6.2 } as const;

function mesh(geometry: BoxGeometry | CylinderGeometry, material: Material, x: number, y: number, z: number): Mesh {
  const m = new Mesh(geometry, material);
  m.position.set(x, y, z);
  m.castShadow = true;
  return m;
}

/** A tall unipole billboard, printed both sides (the same board is seen coming back). */
export function buildBillboard(face: Material): Group {
  const group = new Group();
  const { width: w, height: h, poleHeight: p } = BILLBOARD;
  group.add(mesh(new CylinderGeometry(0.35, 0.45, p, 10), STEEL, 0, p / 2, 0));
  group.add(mesh(new BoxGeometry(w + 0.5, h + 0.5, 0.4), FRAME, 0, p + h / 2, 0));
  const front = new Mesh(new PlaneGeometry(w, h), face);
  front.position.set(0, p + h / 2, 0.21);
  const back = new Mesh(new PlaneGeometry(w, h), face);
  back.position.set(0, p + h / 2, -0.21);
  back.rotation.y = Math.PI;
  group.add(front, back);
  // A little catwalk under the board, like the real ones.
  group.add(mesh(new BoxGeometry(w, 0.1, 0.8), FRAME, 0, p - 0.1, 0.4));
  return group;
}

/**
 * An overhead gantry across both carriageways (like the signs at the Airport), with the ad over our lanes
 * facing us and a second face over the opposite carriageway for traffic coming the other way.
 */
export function buildGantry(face: Material): Group {
  const group = new Group();
  const leftX = LAYOUT.leftSidewalkX;
  const rightX = LAYOUT.sidewalkX;
  const top = GANTRY.clearance + GANTRY.height + 0.4;
  for (const x of [leftX, rightX]) group.add(mesh(new BoxGeometry(0.5, top, 0.5), STEEL, x, top / 2, 0));
  const span = rightX - leftX;
  group.add(mesh(new BoxGeometry(span, 0.35, 0.35), STEEL, (leftX + rightX) / 2, top, 0));
  group.add(mesh(new BoxGeometry(span, 0.25, 0.25), STEEL, (leftX + rightX) / 2, GANTRY.clearance - 0.2, 0));
  const ours = new Mesh(new PlaneGeometry(GANTRY.width, GANTRY.height), face);
  ours.position.set(0, GANTRY.clearance + GANTRY.height / 2, 0.2);
  const theirs = new Mesh(new PlaneGeometry(GANTRY.width, GANTRY.height), face);
  theirs.position.set(LAYOUT.oppositeRoadX, GANTRY.clearance + GANTRY.height / 2, -0.2);
  theirs.rotation.y = Math.PI;
  group.add(mesh(new BoxGeometry(GANTRY.width + 0.4, GANTRY.height + 0.4, 0.3), FRAME, 0, GANTRY.clearance + GANTRY.height / 2, 0));
  group.add(mesh(new BoxGeometry(GANTRY.width + 0.4, GANTRY.height + 0.4, 0.3), FRAME, LAYOUT.oppositeRoadX, GANTRY.clearance + GANTRY.height / 2, 0));
  group.add(ours, theirs);
  return group;
}

/** Ad faces are lit like printed vinyl: slightly glossy, never pitch black at night. */
export function adFaceMaterial(): MeshStandardMaterial {
  return new MeshStandardMaterial({ roughness: 0.55, metalness: 0, side: DoubleSide, emissive: '#ffffff', emissiveIntensity: 0.12 });
}
